import role12MfrsSchema from '../../../../assets/forms/role-310000-mfrs.form.json';
import role12MpersSchema from '../../../../assets/forms/role-310000-mpers.form.json';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { isEmptyCell } from '../validation-field-key.util';
import {
  applyLinkedInheritance,
  buildEqualitySnapshot,
  calculator,
  type LinkableControl,
} from './calc-diff.helper';
import { ROLE13_ANALYSIS_LINK_ROLE } from './link-roles';

/**
 * Shared business rules for Role 12 (Statement of Profit or Loss).
 *
 * Role 12 is an accounting-standard dependent sheet: the same logical sheet
 * ships as two sibling form JSONs — role-310000-mfrs.form.json and
 * role-310000-mpers.form.json — that differ in qname prefixes (ifrs-full:* /
 * ssmt-mfrs:* vs ifrs-smes:* / ssmt-mpers:*) AND in row composition (MFRS has
 * extra rows like FairValuationGainLoss, ShareOfProfitOfAssociates, more EPS
 * detail). Mirrors role 13's selection pattern.
 *
 * Both role12-310000-statement-of-profit-or-loss.component.ts AND
 * role12StatementOfProfitOrLossValidator import from this file so the schema
 * selection literal and shared types live in exactly one place. If the
 * backend ever renames the accounting-standard enum, only this file changes —
 * the parity-check harness will catch any consumer that drifted.
 *
 * ---- Selection semantics (standard MFRS-default convention) --------------
 *
 * Role 12 now follows the SAME MFRS-default convention as every other variant
 * sheet (role 8 / 15 / 19 / 20 / 23 / 24): the shared `pickSchema(std, mfrs,
 * mpers)` returns the MPERS schema ONLY on an exact match against the shared
 * `MPERS_ACCOUNTING_STANDARD` plural literal; any other value (including
 * undefined and '') selects MFRS. Previously role 12 inverted this (MPERS as
 * the fallback, MFRS on exact match) which made an empty/unknown standard
 * silently load MPERS — that inversion has been removed.
 *
 * ---- Cross-sheet dependency surface --------------------------------------
 *
 * Role 12 reads the following slices for its OWN evaluation:
 *
 *   1. `filingPrerequisites` (Role 0) — drives:
 *        - `NatureOfFinancialStatements` ("Consolidated" / "Separate") which
 *          (a) toggles whether Consolidated columns are visible at all and
 *          (b) makes the two `Profit (loss) attributable to ...` rows
 *          mandatory via the schema's `requiredIf`.
 *        - `DisclosureOfFinancialStatementsPreparationForCurrentSubmission`
 *          which decides whether PreviousYear columns are visible.
 *
 *   2. `scopeOfFiling` (Role 2) — `DisclosureOfWhetherFinancialStatementsAreConsolidated`
 *      sets the consolidation status. The component reads this in
 *      `checkConsolidatedStatus()` but only uses it to populate
 *      `isConsolidated` (UI display); column visibility is driven by
 *      filingPrerequisites NatureOfFinancialStatements. Kept here for
 *      documentation since the cross-tab re-validation pipeline subscribes
 *      to BOTH slices.
 *
 *   3. `analysisOfProfitOrLoss` (Role 13) — UPSTREAM data feed. Role 13's
 *      calculated totals (Revenue, CostOfSales, OtherIncome, OtherExpense,
 *      FinanceIncome, ...) are pulled into Role 12 via `linkTo` declarations
 *      in the schema. From Role 12's PURE VALIDATION perspective these
 *      values arrive in `sheetData` already merged by the component's
 *      pullRole13Values() — the editor flushes them to state before the
 *      validator runs. The validator does NOT need to read role 13 from
 *      crossSheetData because (a) the linked fields are persisted on Role
 *      12's own slice once the component has run and (b) the orchestrator's
 *      cache-miss path uses whatever is on disk. Role 13's calculated
 *      values participate only in calculation, not in row-level Failed
 *      drivers, so omitting the cross-sheet read here doesn't change parity.
 *
 *   4. `statementOfCashFlowsIndirectMethod` (Role 18) — Role 12's
 *      `ProfitLossBeforeTax` row declares an `equality` rule against Role
 *      18's same field for cross-sheet reconciliation. Mismatch above
 *      tolerance → Failed row. (Role 18 is being lifted in a sibling batch;
 *      role 12 must NOT depend on role 18's validator output, only on its
 *      data.)
 *
 *   5. `statementOfChangesInEquity` (Role 19) — Role 12's `ProfitLoss` row
 *      declares an `equality` rule against Role 19's `ProfitLoss` for
 *      cross-sheet reconciliation. Mismatch → Failed row. Role 19 stores
 *      data with equity-axis suffixes; the lookup probes the same
 *      candidate keys as the role 15 lift (EquityMember /
 *      EquityAttributableToOwnersOfParentMember / legacy dual-column).
 *
 * Per `SheetDependencyService`:
 *   statementOfProfitOrLoss -> [statementOfCashFlowsIndirectMethod]
 *   analysisOfProfitOrLoss  -> [statementOfProfitOrLoss]
 * i.e. role 12 invalidates role 18; role 13 invalidates role 12.
 *
 * ---- Same-sheet ProfitLoss ↔ ProfitLoss_2 cross-validation --------------
 *
 * Both schemas declare an `equality` rule on `ProfitLoss` referencing
 * `ProfitLoss_2` (and vice-versa) — both fields are calculated in
 * CalculationService.calculateTotals_310000:
 *   ProfitLoss   = ProfitLossFromContinuingOperations + ProfitLossFromDiscontinuedOperations
 *   ProfitLoss_2 = AttributableOwnersParent + AttributableEquityOther + AttributableNCI
 * A genuine difference between the two indicates the user's attribution
 * inputs do not roll up to the calculated profit total. The component
 * raises this as an error in `validateAgainstApiData()` (search for
 * "Cross-validation"). The pure validator mirrors that branch.
 */

/**
 * Literal value of
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MFRS schema. See CrossReferenceService.isMFRS() for the
 * source of truth.
 */
export const ROLE12_MFRS_ACCOUNTING_STANDARD =
  'Malaysian Financial Reporting Standards';

/**
 * Literal selecting the MPERS schema. Re-exported from the shared
 * `schema-picker.util` so the string lives in exactly one place.
 */
export const ROLE12_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE12_MFRS_SCHEMA_FILE = 'role-310000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE12_MPERS_SCHEMA_FILE = 'role-310000-mpers.form.json';

/**
 * Column-visibility gating for Role 12 now lives in the shared, config-driven
 * resolver: see `dimension-visibility.util.ts` (`resolveDimensionVisibility`)
 * and the central `dimension-visibility-registry.ts`
 * (`SHEET_KEYS.statementOfProfitOrLoss` → NATURE / PREP gates). Both the
 * mounted editor (via `CrossReferenceService.getVisibility`) and the pure
 * `role12StatementOfProfitOrLossValidator` call that one resolver, so the
 * former role-local `ROLE12_NATURE_*` / `ROLE12_PREPARATION_*` constants,
 * `Role12ColumnVisibility`, `resolveRole12PrerequisiteValue`, and
 * `resolveRole12ColumnVisibility` were removed to keep a single source.
 */

export type Role12Dimension = 'Consolidated' | 'Separate';
export type Role12Period = 'CurrentYear' | 'PreviousYear';

/**
 * Narrow shape of a role 12 schema control used by the pure validator.
 *
 * Mirrors the FieldControl interface in the editor component. Only the fields
 * the validator inspects are typed.
 */
export interface Role12SchemaValidator {
  name?: string;
  conditionalOn?: unknown;
}

export interface Role12SchemaEqualityRule {
  sheet: string;
  field: string;
  /** Optional per-rule tolerance; defaults to 0.001 in the editor. */
  tolerance?: number;
  /** Optional per-rule message; defaults to a generated one. */
  message?: string;
  /** Optional taxonomy filter (MFRS / MPERS). */
  taxonomy?: string;
  skipIfSourceNull?: boolean;
  skipIfTargetNull?: boolean;
}

export interface Role12SchemaControl {
  qname: string;
  label?: string;
  type?: string;
  required?: boolean;
  validators?: Role12SchemaValidator[];
  ui?: {
    controlType?: string;
    level?: number;
    dualColumn?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    linkTo?: { role: string; qname: string };
  };
  requiredIf?: {
    sheet: string;
    field: string;
    equals: string;
    errorMessage?: string;
  };
  validation?: {
    dataType?: string;
    equality?: Role12SchemaEqualityRule[];
  };
}

export interface Role12Schema {
  roleId?: string;
  taxonomy?: string;
  sheetId?: string;
  label?: string;
  description?: string;
  controls: Role12SchemaControl[];
}

/**
 * Resolve the correct role 12 schema filename for the given accounting
 * standard. Mirrors CrossReferenceService.isMFRS() so the component's
 * fetch() and the validator's static import both derive from the same rule.
 */
export function pickRole12SchemaFile(
  accountingStandard: string | undefined | null
): string {
  // Standard MFRS-default convention: MPERS only on the exact shared MPERS
  // literal, MFRS for everything else (incl. undefined/'').
  return pickSchema(
    accountingStandard,
    ROLE12_MFRS_SCHEMA_FILE,
    ROLE12_MPERS_SCHEMA_FILE
  );
}

/**
 * Resolve the actual role 12 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-
 * time (the listing/dashboard cache-miss path runs synchronously) so both
 * variants are imported eagerly and selected from memory.
 */
export function pickRole12Schema(
  accountingStandard: string | undefined | null
): Role12Schema {
  // Standard MFRS-default convention (see "Selection semantics" above): MFRS
  // is the first value (the default for unknown/empty input) and MPERS the
  // second (chosen only on an exact match against the shared MPERS literal).
  return pickSchema(
    accountingStandard,
    role12MfrsSchema as unknown as Role12Schema,
    role12MpersSchema as unknown as Role12Schema
  );
}

/**
 * Returns true when the accounting standard resolves to MFRS. Mirrors the
 * standard MFRS-default convention used by every other variant sheet: MFRS for
 * anything that is NOT the exact shared MPERS literal (incl. undefined/'').
 */
export function isRole12MFRS(
  accountingStandard: string | undefined | null
): boolean {
  return accountingStandard !== ROLE12_MPERS_ACCOUNTING_STANDARD;
}

/**
 * Header detection. Mirrors the component's `isSectionHeader()` so the
 * validator skips the same controls the editor skips when building the form.
 */
export function isRole12SectionHeader(control: Role12SchemaControl): boolean {
  return (
    control.type === 'SectionHeader' ||
    control.type === 'TextBlock' ||
    control.ui?.controlType === 'section-header'
  );
}

/**
 * Is this control a calculated / readonly total? Calculated fields are
 * populated by CalculationService.calculateTotals_310000 at runtime and
 * are not user-editable; required-field checks should skip them so the
 * pure validator never flags emptiness on a row that the calculation
 * engine will fill in. Mirrors the editor's filterOutLinkedFields() +
 * isCalculatedField() guard pattern.
 */
export function isRole12CalculatedControl(
  control: Role12SchemaControl
): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/**
 * Schema controls that participate in validation. Excludes section headers
 * exactly as the component's buildForm() does. Section headers never become
 * FormControls and therefore cannot fail validation.
 */
export function getRole12ValidatingControls(
  schema: Role12Schema
): ReadonlyArray<Role12SchemaControl> {
  return schema.controls.filter((control) => !isRole12SectionHeader(control));
}

/**
 * Build the bracket-notation form key the component uses for a row+column
 * cell: `qname[Consolidated|Separate][CurrentYear|PreviousYear]`. Mirrors
 * the component's getXbrlControlName() helper.
 */
export function buildRole12ColumnKey(
  qname: string,
  dimension: Role12Dimension,
  period: Role12Period
): string {
  return `${qname}[${dimension}][${period}]`;
}

/**
 * Check if a single form-shaped value counts as "empty" for the purposes of
 * required-field validation. Matches the component's
 *   formControl.value === null
 * guard in `validateMandatoryFields()` — note the editor intentionally treats
 * only the literal `null` as missing (zero is a valid monetary value), so we
 * mirror that here. We also accept undefined and '' since orchestrator-loaded
 * payloads may surface either when the form control was never persisted.
 */
export function isRole12Empty(value: unknown): boolean {
  return isEmptyCell(value);
}

/**
 * Build the candidate Role 19 lookup keys for a given Role 12 base qname,
 * dimension, and period. Role 19 (SOCIE) stores its data with an
 * equity-axis suffix that varies by template (EquityMember vs
 * EquityAttributableToOwnersOfParentMember). Mirrors the role 15 lift's
 * `buildRole19CandidateKeys()` and the validation-warning service's
 * `buildFieldKey()` for `equityColumns` sheets. Order matters: first match
 * wins.
 */
export function buildRole19CandidateKeys(
  baseQname: string,
  dimension: Role12Dimension,
  period: Role12Period,
  accountingStandard: string | undefined | null
): string[] {
  const mfrs = isRole12MFRS(accountingStandard);
  const equityMember = mfrs ? 'ifrs-full:EquityMember' : 'ifrs-smes:EquityMember';
  const equityAttributable = mfrs
    ? 'ifrs-full:EquityAttributableToOwnersOfParentMember'
    : 'ifrs-smes:EquityAttributableToOwnersOfParentMember';

  return [
    `${baseQname}:${equityMember}[${dimension}][${period}]`,
    `${baseQname}:${equityAttributable}[${dimension}][${period}]`,
    // Legacy dual-column fallback — preserved for back-compat with older
    // payloads.
    `${baseQname}[${dimension}][${period}]`,
  ];
}

/**
 * Build the candidate dual-column lookup key for a Role 18 (cash flows
 * indirect) cell. Role 18 is a `dualColumn` sheet per the validation-warning
 * service's SHEET_COLUMN_TYPE_MAP, so its keys use the plain
 * `qname[Dimension][Period]` shape.
 */
export function buildRole18Key(
  baseQname: string,
  dimension: Role12Dimension,
  period: Role12Period
): string {
  return `${baseQname}[${dimension}][${period}]`;
}

/**
 * The result of replaying Role 12's own recompute over Role-13-inherited leaves.
 * All three shapes are needed by callers, so the recipe returns them together
 * rather than making anyone re-derive one from another:
 *   - `leaves`     — Role 12's persisted cells with the Role-13 leaves overlaid;
 *   - `recomputed` — ONLY the calculated totals (what `hasCalculatedFieldMismatch`
 *     must be handed; passing the merged snapshot would diff raw leaves too);
 *   - `snapshot`   — `leaves` with `recomputed` overlaid, i.e. the operand bag a
 *     blocking equality check reads.
 */
export interface Role12RecomputeResult {
  leaves: Record<string, any>;
  recomputed: Record<string, any>;
  snapshot: Record<string, any>;
}

/**
 * Replay Role 12's totals over Role-13-inherited leaves — the ONE copy of that
 * recipe.
 *
 * WHY IT IS SHARED — the editor never reads Role 13's RAW slice: the filing-form
 * container recomputes Role 13 at load (`calculateTotals_310100`) and persists
 * it, and `pullRole13Values()` pulls the RECOMPUTED totals into Role 12. Any
 * headless path that skips that step inherits the raw extraction, computes a
 * different `Profit (loss)`, and publishes a verdict the editor contradicts the
 * moment the sheet is opened.
 *
 * Two callers need it, which is why it lives here rather than inside the Role-12
 * validator:
 *   - `role12StatementOfProfitOrLossValidator`, for its own equality operands;
 *   - `role15StatementOfComprehensiveIncomeValidator`, because Role 15's
 *     `Profit (loss)` INHERITS from this sheet — making Role 12 an inheritance
 *     source that is itself an inheriting sheet (SOCI <- SOPL <- Analysis of P&L).
 *
 * A missing/empty `role13Data` degrades to Role 12's own leaves, matching
 * `applyLinkedInheritance`'s tolerated-absent behaviour.
 */
export function recomputeRole12WithRole13Inheritance(
  role12Data: Record<string, any>,
  role13Data: Record<string, any> | undefined,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
  accountingStandard: string | undefined | null
): Role12RecomputeResult {
  const recomputedRole13 =
    role13Data && Object.keys(role13Data).length > 0
      ? buildEqualitySnapshot(
          role13Data,
          calculator.calculateTotals_310100(role13Data, xbrlPrefix)
        )
      : role13Data;

  const leaves = applyLinkedInheritance(
    pickRole12Schema(accountingStandard).controls as unknown as LinkableControl[],
    role12Data,
    recomputedRole13,
    ROLE13_ANALYSIS_LINK_ROLE
  );

  const recomputed = calculator.calculateTotals_310000(leaves, xbrlPrefix);

  return { leaves, recomputed, snapshot: buildEqualitySnapshot(leaves, recomputed) };
}
